import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  UploadIcon,
  MicrophoneIcon,
  MicrophoneFilledIcon,
  LightbulbIcon,
  TranslateIcon,
} from "@/components/Icons";
import { OnboardingIntent } from "@/models/onboardingPersona";

type IconType = (props: { size?: number; className?: string }) => ReactNode;

const INTENT_ICONS: Record<OnboardingIntent, { Icon: IconType; tint: string }> =
  {
    import: { Icon: UploadIcon, tint: "text-blue-500" },
    record: { Icon: MicrophoneIcon, tint: "text-rose-500" },
    dictate: { Icon: MicrophoneFilledIcon, tint: "text-violet-500" },
    study: { Icon: LightbulbIcon, tint: "text-amber-500" },
    translate: { Icon: TranslateIcon, tint: "text-emerald-500" },
  };

type Props = {
  actionId?: string;
  value: OnboardingIntent | null;
  onChange: (intent: OnboardingIntent) => void;
  /** Ordered list — pass `getIntentOrder(persona)` to lead with the
   * persona's highest-affinity job. */
  order: ReadonlyArray<OnboardingIntent>;
};

const IntentPicker = ({ value, onChange, order }: Props) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 w-full">
      {order.map((intent) => {
        const { Icon, tint } = INTENT_ICONS[intent];
        const isSelected = value === intent;
        return (
          <button
            key={intent}
            type="button"
            data-action-id="onboarding.intent.select"
            aria-pressed={isSelected}
            onClick={() => onChange(intent)}
            className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 active:scale-[0.98] w-full text-start ${
              isSelected ? "selected-surface scale-[1.01]" : "glass-tile"
            }`}
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                isSelected ? "bg-[var(--accent-bg)]" : "bg-[var(--fill-1)]"
              }`}
            >
              <Icon
                size={18}
                className={isSelected ? "text-[var(--accent)]" : tint}
              />
            </div>
            <div className="flex flex-col min-w-0">
              <span
                className={`text-[15px] font-semibold leading-tight ${
                  isSelected
                    ? "text-[var(--text-primary)]"
                    : "text-[var(--text-secondary)]"
                }`}
              >
                {t(`onboarding.intent.label_${intent}`)}
              </span>
              <span className="text-[12px] text-[var(--text-tertiary)] leading-tight mt-0.5">
                {t(`onboarding.intent.sub_${intent}`)}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default IntentPicker;
